import React, { useEffect, useState, useRef } from "react";
import { PageHeader } from "../components/Layout";
import { api, apiErr } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import {
  Terminal, Copy, Download, Wand2, Wifi, Server, Save, Loader2, Cpu, Network,
  Router as RouterIcon, Globe, Shield, CheckCircle2, AlertTriangle, ChevronLeft,
  ChevronRight, History, FileArchive, UploadCloud, Package, Upload, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

const DEFAULT_CFG = {
  model: "",
  wan_port: "ether1",
  wan_mode: "dhcp",
  wan_ip: "",
  wan_gateway: "",
  wan_dns: "8.8.8.8,1.1.1.1",
  pppoe_user: "",
  pppoe_password: "",
  bridge_name: "bridge-hotspot",
  bridge_ports: [],
  gateway_ip: "10.5.50.1",
  subnet_cidr: "10.5.50.0/24",
  pool_start: "10.5.50.10",
  pool_end: "10.5.50.254",
  dns_servers: "8.8.8.8,1.1.1.1",
  dns_name: "hotspot.goweb.local",
  lease_time: "1h",
  nat_masquerade: true,
  hotspot_profile_name: "goweb-hsprof",
  login_by: "http-chap",
  use_radius_accounting: true,
  walled_garden: "",
  nas_identifier: "",
};

const STEPS = [
  { id: 1, label: "Modello", icon: Cpu },
  { id: 2, label: "WAN", icon: Globe },
  { id: 3, label: "Bridge", icon: Network },
  { id: 4, label: "Rete Hotspot", icon: Wifi },
  { id: 5, label: "NAT", icon: Shield },
  { id: 6, label: "Verifica", icon: CheckCircle2 },
];

function Field({ label, value, onChange, placeholder, testid, mono = true }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
      <input data-testid={testid} value={value ?? ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm ${mono ? "font-mono" : ""} focus:outline-none focus:ring-2 focus:ring-blue-500`} />
    </div>
  );
}

export default function MikrotikConfigurator() {
  const { t } = useI18n();
  const { effectiveStructure } = useAuth();
  const [routers, setRouters] = useState([]);
  const [routerId, setRouterId] = useState("");
  const [models, setModels] = useState([]);
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [step, setStep] = useState(1);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [versions, setVersions] = useState([]);
  const [applyingId, setApplyingId] = useState("");
  const fileRef = useRef(null);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const model = models.find((m) => m.name === cfg.model);
  const modelPorts = model?.ports || [];
  const selectedRouter = routers.find((r) => r.id === routerId);

  useEffect(() => {
    const p = effectiveStructure ? `?structure_id=${effectiveStructure}` : "";
    api.get(`/routers${p}`).then((r) => { setRouters(r.data); setRouterId(r.data[0]?.id || ""); });
    api.get("/mikrotik/models").then((r) => setModels(r.data));
    setCfg((c) => ({ ...c, walled_garden: c.walled_garden || window.location.host }));
  }, [effectiveStructure]);

  const loadVersions = (rid) => {
    if (!rid) { setVersions([]); return; }
    api.get(`/routers/${rid}/config-versions`).then((r) => setVersions(r.data)).catch(() => setVersions([]));
  };
  useEffect(() => { loadVersions(routerId); }, [routerId]);

  const pickModel = (m) => {
    set({ model: m.name, wan_port: m.wan_default, bridge_ports: m.ports.filter((p) => p !== m.wan_default) });
  };

  const toggleBridgePort = (port) => {
    setCfg((c) => {
      const has = c.bridge_ports.includes(port);
      return { ...c, bridge_ports: has ? c.bridge_ports.filter((p) => p !== port) : [...c.bridge_ports, port] };
    });
  };

  const generate = async (override) => {
    const body = override || cfg;
    if (!routerId) { toast.error(t("mkcfg.pick_router")); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`/routers/${routerId}/generate-config`, body);
      setResult(data);
      if (data.valid) toast.success(t("mkcfg.generated"));
      else toast.error("Correggi gli errori di validazione");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const importRsc = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!routerId) { toast.error(t("mkcfg.pick_router")); return; }
    const text = await file.text();
    try {
      const { data } = await api.post(`/routers/${routerId}/config/import`, { content: text });
      const merged = { ...DEFAULT_CFG, ...data.config };
      setCfg(merged);
      setStep(6);
      await generate(merged);
      if (data.warnings?.length) toast.warning(data.warnings.join(" "));
      else toast.success("Configurazione .rsc importata");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const rollbackVersion = async (v) => {
    setApplyingId(v.id);
    try {
      const { data } = await api.post(`/routers/${routerId}/config-versions/${v.id}/rollback`);
      const merged = { ...DEFAULT_CFG, ...data.config };
      setCfg(merged);
      setStep(6);
      await generate(merged);
      toast.success(data.message);
      loadVersions(routerId);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setApplyingId(""); }
  };

  const saveVersion = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/routers/${routerId}/config-versions`, cfg);
      toast.success(`Versione v${data.version} salvata`);
      loadVersions(routerId);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const applyVersion = async (v) => {
    setApplyingId(v.id);
    try {
      const { data } = await api.post(`/routers/${routerId}/config-versions/${v.id}/apply`);
      toast.success(data.message);
      loadVersions(routerId);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setApplyingId(""); }
  };

  const downloadBlob = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = href; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(href);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail || "Download fallito")); }
  };

  const downloadLoginHtml = () => {
    if (!selectedRouter) { toast.error(t("mkcfg.pick_router")); return; }
    downloadBlob(
      `/structures/${selectedRouter.structure_id}/hotspot-login-html?portal_base=${encodeURIComponent(window.location.origin)}`,
      "login.html");
  };

  const copyScript = () => { navigator.clipboard.writeText(result.script); toast.success(t("mkcfg.copy") + " ✓"); };
  const downloadScript = () => {
    const blob = new Blob([result.script], { type: "text/plain" });
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href; a.download = result.filename;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(href);
  };

  const canNext = () => {
    if (step === 1) return !!cfg.model;
    if (step === 3) return cfg.bridge_ports.length > 0 && !cfg.bridge_ports.includes(cfg.wan_port);
    return true;
  };
  const next = () => { if (step < 6) setStep(step + 1); if (step === 5) generate(); };
  const prev = () => step > 1 && setStep(step - 1);

  return (
    <div data-testid="mkconfig-page">
      <PageHeader title={t("mkcfg.title")} subtitle="Procedura guidata: Modello → WAN → Bridge → Rete → NAT → Verifica" />

      {/* Router selector */}
      <div className="rounded-xl border border-[#1F2937] bg-[#111827] p-4 mb-4 flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-2 text-sm font-semibold"><RouterIcon className="h-4 w-4 text-cyan-400" /> {t("routers.title")}</span>
        <select data-testid="mkcfg-router-select" value={routerId} onChange={(e) => setRouterId(e.target.value)}
          className="rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm min-w-[280px]">
          {routers.map((r) => <option key={r.id} value={r.id}>{r.name} — {r.host} ({r.status})</option>)}
          {routers.length === 0 && <option value="">—</option>}
        </select>
        {selectedRouter && (
          <span className={`text-xs px-2 py-1 rounded-full ${selectedRouter.status === "online" ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-500/15 text-slate-400"}`}>
            {selectedRouter.status}
          </span>
        )}
        <div className="ml-auto flex gap-2">
          <input ref={fileRef} type="file" accept=".rsc,.txt" onChange={importRsc} className="hidden" data-testid="import-file-input" />
          <button data-testid="import-rsc-btn" onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600/20 text-violet-300 ring-1 ring-violet-600/30 hover:bg-violet-600/30 px-3 py-2 text-xs font-semibold transition-colors">
            <Upload className="h-4 w-4" /> Importa .rsc
          </button>
          <button data-testid="download-loginhtml-btn" onClick={downloadLoginHtml}
            className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-600/20 text-cyan-300 ring-1 ring-cyan-600/30 hover:bg-cyan-600/30 px-3 py-2 text-xs font-semibold transition-colors">
            <Download className="h-4 w-4" /> login.html captive
          </button>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-1 mb-5 overflow-x-auto pb-1">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const active = step === s.id, done = step > s.id;
          return (
            <React.Fragment key={s.id}>
              <button data-testid={`step-tab-${s.id}`} onClick={() => setStep(s.id)}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold whitespace-nowrap transition-colors ${active ? "bg-blue-600 text-white" : done ? "bg-emerald-600/15 text-emerald-300" : "bg-white/5 text-slate-400 hover:bg-white/10"}`}>
                <Icon className="h-4 w-4" /> <span className="hidden sm:inline">{s.id}. {s.label}</span><span className="sm:hidden">{s.id}</span>
              </button>
              {i < STEPS.length - 1 && <ChevronRight className="h-3.5 w-3.5 text-slate-600 shrink-0" />}
            </React.Fragment>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left — wizard steps */}
        <div className="rounded-xl border border-[#1F2937] bg-[#111827] p-6 space-y-4 min-h-[460px]">
          {/* STEP 1 — Modello */}
          {step === 1 && (
            <div data-testid="wizard-step-1" className="space-y-3">
              <h3 className="font-display font-semibold flex items-center gap-2"><Cpu className="h-4.5 w-4.5 text-cyan-400" /> Modello hardware</h3>
              <p className="text-xs text-slate-500">Seleziona il modello: verranno mostrate le porte disponibili.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {models.map((m) => (
                  <button key={m.id} data-testid={`model-${m.id}`} onClick={() => pickModel(m)}
                    className={`text-left rounded-lg border p-3 transition-colors ${cfg.model === m.name ? "border-blue-500 bg-blue-600/10" : "border-[#1F2937] bg-[#0B0F17] hover:border-slate-600"}`}>
                    <div className="font-semibold text-sm flex items-center gap-2">{m.name} {m.wifi && <Wifi className="h-3.5 w-3.5 text-cyan-400" />}</div>
                    <div className="text-[11px] text-slate-500 mt-1">{m.ports.length} porte · RouterOS {m.ros}</div>
                    <div className="text-[10px] font-mono text-slate-600 mt-1 truncate">{m.ports.join(", ")}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2 — WAN */}
          {step === 2 && (
            <div data-testid="wizard-step-2" className="space-y-3">
              <h3 className="font-display font-semibold flex items-center gap-2"><Globe className="h-4.5 w-4.5 text-amber-400" /> Configurazione WAN</h3>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Porta WAN</label>
                <select data-testid="wan-port" value={cfg.wan_port}
                  onChange={(e) => set({ wan_port: e.target.value, bridge_ports: cfg.bridge_ports.filter((p) => p !== e.target.value) })}
                  className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm font-mono">
                  {modelPorts.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Modalità</label>
                <div className="mt-1 grid grid-cols-3 gap-2">
                  {["dhcp", "static", "pppoe"].map((mode) => (
                    <button key={mode} data-testid={`wan-mode-${mode}`} onClick={() => set({ wan_mode: mode })}
                      className={`rounded-lg px-3 py-2 text-xs font-semibold uppercase transition-colors ${cfg.wan_mode === mode ? "bg-blue-600 text-white" : "bg-[#0B0F17] border border-[#1F2937] text-slate-400 hover:text-white"}`}>
                      {mode === "dhcp" ? "DHCP Client" : mode === "static" ? "IP Manuale" : "PPPoE"}
                    </button>
                  ))}
                </div>
              </div>
              {cfg.wan_mode === "static" && (
                <div className="grid grid-cols-2 gap-3">
                  <Field testid="wan-ip" label="IP/CIDR" value={cfg.wan_ip} onChange={(v) => set({ wan_ip: v })} placeholder="192.168.1.2/24" />
                  <Field testid="wan-gateway" label="Gateway" value={cfg.wan_gateway} onChange={(v) => set({ wan_gateway: v })} placeholder="192.168.1.1" />
                  <div className="col-span-2"><Field testid="wan-dns" label="DNS WAN" value={cfg.wan_dns} onChange={(v) => set({ wan_dns: v })} placeholder="8.8.8.8,1.1.1.1" /></div>
                </div>
              )}
              {cfg.wan_mode === "pppoe" && (
                <div className="grid grid-cols-2 gap-3">
                  <Field testid="pppoe-user" label="Utente PPPoE" value={cfg.pppoe_user} onChange={(v) => set({ pppoe_user: v })} placeholder="user@isp" />
                  <Field testid="pppoe-password" label="Password PPPoE" value={cfg.pppoe_password} onChange={(v) => set({ pppoe_password: v })} placeholder="••••••" />
                </div>
              )}
              {cfg.wan_mode === "dhcp" && <p className="text-xs text-slate-500 bg-[#0B0F17] rounded-lg p-3 border border-[#1F2937]">La WAN otterrà IP, gateway e DNS automaticamente dal provider (DHCP client con default-route).</p>}
            </div>
          )}

          {/* STEP 3 — Bridge */}
          {step === 3 && (
            <div data-testid="wizard-step-3" className="space-y-3">
              <h3 className="font-display font-semibold flex items-center gap-2"><Network className="h-4.5 w-4.5 text-violet-400" /> Bridge Hotspot (LAN)</h3>
              <Field testid="bridge-name" label="Nome bridge" value={cfg.bridge_name} onChange={(v) => set({ bridge_name: v })} placeholder="bridge-hotspot" />
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Porte LAN nel bridge</label>
                <p className="text-[11px] text-amber-400/80 mb-2">La porta WAN <span className="font-mono">{cfg.wan_port}</span> è esclusa automaticamente.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {modelPorts.map((p) => {
                    const isWan = p === cfg.wan_port;
                    const checked = cfg.bridge_ports.includes(p);
                    return (
                      <label key={p} data-testid={`bridge-port-${p}`}
                        className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-mono cursor-pointer ${isWan ? "opacity-40 cursor-not-allowed border-[#1F2937]" : checked ? "border-violet-500 bg-violet-600/10" : "border-[#1F2937] bg-[#0B0F17] hover:border-slate-600"}`}>
                        <input type="checkbox" disabled={isWan} checked={checked} onChange={() => toggleBridgePort(p)} className="accent-violet-600" />
                        {p} {isWan && <span className="text-[9px] uppercase text-amber-400">WAN</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* STEP 4 — Rete Hotspot */}
          {step === 4 && (
            <div data-testid="wizard-step-4" className="space-y-3">
              <h3 className="font-display font-semibold flex items-center gap-2"><Wifi className="h-4.5 w-4.5 text-cyan-400" /> Rete Hotspot</h3>
              <div className="grid grid-cols-2 gap-3">
                <Field testid="gateway-ip" label="Gateway IP" value={cfg.gateway_ip} onChange={(v) => set({ gateway_ip: v })} placeholder="10.5.50.1" />
                <Field testid="subnet-cidr" label="Subnet (CIDR)" value={cfg.subnet_cidr} onChange={(v) => set({ subnet_cidr: v })} placeholder="10.5.50.0/24" />
                <Field testid="pool-start" label="Pool DHCP inizio" value={cfg.pool_start} onChange={(v) => set({ pool_start: v })} placeholder="10.5.50.10" />
                <Field testid="pool-end" label="Pool DHCP fine" value={cfg.pool_end} onChange={(v) => set({ pool_end: v })} placeholder="10.5.50.254" />
                <Field testid="dns-servers" label="Server DNS" value={cfg.dns_servers} onChange={(v) => set({ dns_servers: v })} placeholder="8.8.8.8,1.1.1.1" />
                <Field testid="lease-time" label="Lease time" value={cfg.lease_time} onChange={(v) => set({ lease_time: v })} placeholder="1h" />
                <Field testid="dns-name" label="DNS name Hotspot" value={cfg.dns_name} onChange={(v) => set({ dns_name: v })} placeholder="hotspot.goweb.local" />
                <Field testid="profile-name" label="Nome profilo Hotspot" value={cfg.hotspot_profile_name} onChange={(v) => set({ hotspot_profile_name: v })} placeholder="goweb-hsprof" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Metodo di login</label>
                <select data-testid="login-by" value={cfg.login_by} onChange={(e) => set({ login_by: e.target.value })}
                  className="mt-1 w-full rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2 text-sm font-mono">
                  <option value="http-chap">http-chap</option>
                  <option value="http-pap">http-pap</option>
                  <option value="mac,http-chap">mac,http-chap</option>
                </select>
              </div>
              <Field testid="walled-garden" label="Walled Garden" value={cfg.walled_garden} onChange={(v) => set({ walled_garden: v })} placeholder="goweb.srl,booking.goweb.srl" />
              <Field testid="nas-id" label="NAS Identifier (opzionale)" value={cfg.nas_identifier} onChange={(v) => set({ nas_identifier: v })} placeholder="hotel-abc-gw1" />
              <label className="flex items-center gap-2 text-sm">
                <input data-testid="accounting" type="checkbox" checked={cfg.use_radius_accounting} onChange={(e) => set({ use_radius_accounting: e.target.checked })} className="accent-blue-600" />
                Abilita accounting RADIUS
              </label>
            </div>
          )}

          {/* STEP 5 — NAT */}
          {step === 5 && (
            <div data-testid="wizard-step-5" className="space-y-3">
              <h3 className="font-display font-semibold flex items-center gap-2"><Shield className="h-4.5 w-4.5 text-emerald-400" /> NAT</h3>
              <label className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer ${cfg.nat_masquerade ? "border-emerald-500 bg-emerald-600/10" : "border-[#1F2937] bg-[#0B0F17]"}`}>
                <input data-testid="nat-masquerade" type="checkbox" checked={cfg.nat_masquerade} onChange={(e) => set({ nat_masquerade: e.target.checked })} className="accent-emerald-600 mt-1" />
                <span>
                  <span className="font-semibold text-sm">Masquerade (srcnat) attivo</span>
                  <p className="text-xs text-slate-500 mt-1">Consigliato: consente agli ospiti Hotspot di navigare tramite l'IP pubblico della WAN. Disattiva solo per topologie routed senza NAT.</p>
                </span>
              </label>
            </div>
          )}

          {/* STEP 6 — Verifica */}
          {step === 6 && (
            <div data-testid="wizard-step-6" className="space-y-3">
              <h3 className="font-display font-semibold flex items-center gap-2"><CheckCircle2 className="h-4.5 w-4.5 text-emerald-400" /> Validazione & Riepilogo</h3>
              {result?.validation ? (
                <>
                  {result.validation.errors.length === 0 ? (
                    <div className="rounded-lg bg-emerald-600/10 border border-emerald-600/30 p-3 text-sm text-emerald-300 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Configurazione valida</div>
                  ) : (
                    <div data-testid="validation-errors" className="rounded-lg bg-rose-600/10 border border-rose-600/30 p-3 text-sm text-rose-300 space-y-1">
                      {result.validation.errors.map((e, i) => <div key={i} className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {e}</div>)}
                    </div>
                  )}
                  {result.validation.warnings.length > 0 && (
                    <div className="rounded-lg bg-amber-600/10 border border-amber-600/30 p-3 text-xs text-amber-300 space-y-1">
                      {result.validation.warnings.map((w, i) => <div key={i} className="flex items-start gap-2"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {w}</div>)}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs text-slate-500">Premi "Genera & Verifica" per validare e produrre lo script.</p>
              )}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <Info label="Modello" value={cfg.model} />
                <Info label="WAN" value={`${cfg.wan_mode} · ${cfg.wan_port}`} />
                <Info label="Bridge" value={cfg.bridge_ports.join(", ") || "—"} />
                <Info label="Gateway" value={cfg.gateway_ip} />
                <Info label="Pool" value={`${cfg.pool_start}-${cfg.pool_end}`} />
                <Info label="NAT" value={cfg.nat_masquerade ? "masquerade" : "off"} />
              </div>
              <button data-testid="mkcfg-generate-btn" onClick={() => generate()} disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-500 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Genera & Verifica
              </button>
              {result?.valid && (
                <button data-testid="save-version-btn" onClick={saveVersion} disabled={saving}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600/20 text-emerald-300 ring-1 ring-emerald-600/30 hover:bg-emerald-600/30 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva come nuova versione
                </button>
              )}
            </div>
          )}

          {/* Nav */}
          <div className="flex items-center justify-between pt-2 border-t border-[#1F2937] mt-4">
            <button data-testid="wizard-prev" onClick={prev} disabled={step === 1}
              className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-2 text-sm font-medium transition-colors disabled:opacity-40"><ChevronLeft className="h-4 w-4" /> Indietro</button>
            {step < 6 && (
              <button data-testid="wizard-next" onClick={next} disabled={!canNext()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-40">Avanti <ChevronRight className="h-4 w-4" /></button>
            )}
          </div>
        </div>

        {/* Right — output + versions */}
        <div className="space-y-6">
          <div className="rounded-xl border border-[#1F2937] bg-[#111827] p-6">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="font-display font-semibold flex items-center gap-2"><Terminal className="h-4.5 w-4.5 text-emerald-400" /> {t("mkcfg.script")}</h3>
              {result?.script && (
                <div className="flex gap-2">
                  <button data-testid="mkcfg-copy-btn" onClick={copyScript} className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-1.5 text-xs font-medium transition-colors"><Copy className="h-3.5 w-3.5" /> {t("mkcfg.copy")}</button>
                  <button data-testid="mkcfg-download-btn" onClick={downloadScript} className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-1.5 text-xs font-medium transition-colors"><Download className="h-3.5 w-3.5" /> .rsc</button>
                </div>
              )}
            </div>
            {result?.script ? (
              <pre data-testid="mkcfg-output" className="rounded-lg bg-[#0B0F17] border border-[#1F2937] p-4 text-xs font-mono text-emerald-300 whitespace-pre-wrap overflow-x-auto max-h-[420px] overflow-y-auto">{result.script}</pre>
            ) : (
              <div className="flex flex-col items-center justify-center h-[280px] text-slate-600">
                <Terminal className="h-10 w-10 mb-3" />
                <p className="text-sm">{t("mkcfg.empty")}</p>
              </div>
            )}
          </div>

          {/* Versions */}
          <div className="rounded-xl border border-[#1F2937] bg-[#111827] p-6">
            <h3 className="font-display font-semibold flex items-center gap-2 mb-3"><History className="h-4.5 w-4.5 text-amber-400" /> Storico versioni</h3>
            {versions.length === 0 ? (
              <p className="text-xs text-slate-500">Nessuna versione salvata per questo router.</p>
            ) : (
              <div className="space-y-2" data-testid="versions-list">
                {versions.map((v) => (
                  <div key={v.id} data-testid={`version-${v.version}`} className="rounded-lg bg-[#0B0F17] border border-[#1F2937] p-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold flex items-center gap-2">
                        v{v.version}
                        {v.applied && <span className="text-[9px] uppercase bg-emerald-500/15 text-emerald-300 px-1.5 py-0.5 rounded-full">attiva</span>}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">{v.summary?.model} · {v.summary?.wan?.mode}/{v.summary?.wan?.port} · {v.author_email} · {(v.created_at || "").slice(0, 16).replace("T", " ")}</div>
                    </div>
                    <div className="flex gap-1.5">
                      <button data-testid={`version-download-${v.version}`} title=".rsc" onClick={() => downloadBlob(`/routers/${routerId}/config-versions/${v.id}/download`, `v${v.version}_${v.filename}`)}
                        className="rounded-lg bg-white/5 hover:bg-white/10 p-2 transition-colors"><Download className="h-3.5 w-3.5" /></button>
                      <button data-testid={`version-zip-${v.version}`} title=".zip" onClick={() => downloadBlob(`/routers/${routerId}/config-versions/${v.id}/export`, `goweb_config_v${v.version}.zip`)}
                        className="rounded-lg bg-white/5 hover:bg-white/10 p-2 transition-colors"><FileArchive className="h-3.5 w-3.5" /></button>
                      <button data-testid={`version-rollback-${v.version}`} title="Ripristina come attiva" onClick={() => rollbackVersion(v)} disabled={applyingId === v.id}
                        className="rounded-lg bg-amber-600/20 text-amber-300 ring-1 ring-amber-600/30 hover:bg-amber-600/30 p-2 transition-colors disabled:opacity-60">
                        {applyingId === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                      </button>
                      <button data-testid={`version-apply-${v.version}`} title="Applica al MikroTik" onClick={() => applyVersion(v)} disabled={applyingId === v.id}
                        className="rounded-lg bg-emerald-600/20 text-emerald-300 ring-1 ring-emerald-600/30 hover:bg-emerald-600/30 p-2 transition-colors disabled:opacity-60">
                        {applyingId === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-lg bg-[#0B0F17] border border-[#1F2937] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-mono text-cyan-400 truncate">{value || "—"}</div>
    </div>
  );
}
